import { useEffect, useMemo, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { Eyebrow } from "@/components/ui-bits";
import api from "@/lib/api";
import { ArrowLeft, ExternalLink, AlertTriangle, Search, ArrowUpRight } from "lucide-react";

export default function USMLCategory() {
  const { roman } = useParams();
  const romanUpper = String(roman || "").toUpperCase();
  const [entry, setEntry] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    let cancelled = false;
    setEntry(null);
    setNotFound(false);
    (async () => {
      try {
        const r = await api.get(`/usml/categories/${encodeURIComponent(romanUpper)}`);
        if (!cancelled) setEntry(r.data);
      } catch { if (!cancelled) setNotFound(true); }
    })();
    return () => { cancelled = true; };
  }, [romanUpper]);

  const filteredParas = useMemo(() => {
    if (!entry?.paragraph_index) return [];
    if (!q.trim()) return entry.paragraph_index;
    const needle = q.trim().toLowerCase();
    return entry.paragraph_index.filter((p) =>
      p.letter.toLowerCase().includes(needle) ||
      (p.heading || "").toLowerCase().includes(needle) ||
      (p.excerpt || "").toLowerCase().includes(needle)
    );
  }, [entry, q]);

  if (notFound) return <Navigate to="/usml" replace />;

  const lastMod = entry?.last_modified
    ? new Date(entry.last_modified).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
    : "—";
  const sourceAsOf = entry?.source_as_of || "—";

  return (
    <Layout>
      <SEO
        title={entry ? `USML Category ${entry.roman} — ${entry.title}` : `USML Category ${romanUpper}`}
        description={entry ? `Top-level paragraphs (a)–(x) of Category ${entry.roman} of the U.S. Munitions List: ${entry.title}. Mirrored from eCFR.` : ""}
        path={`/usml/${romanUpper}`}
      />

      {/* Persistent disclaimer banner — top */}
      <div className="bg-brass-deep/10 border-b border-brass/40" data-testid="usml-cat-banner-top">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-3 flex items-center gap-3 text-[12px] leading-snug">
          <AlertTriangle className="w-4 h-4 text-brass-deep shrink-0" strokeWidth={2} />
          <p className="text-ink">
            <strong className="uppercase tracking-wide text-[10px]">Mirrored from eCFR{sourceAsOf !== "—" ? ` as of ${sourceAsOf}` : ""}.</strong>{" "}
            This page is a navigational aid, not the authoritative text and not legal advice. Verify against the eCFR.
          </p>
          {entry?.source_url && (
            <a
              href={entry.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-auto inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.22em] text-ink border-b border-brass/60 hover:text-brass hover:border-brass transition-colors whitespace-nowrap"
              data-testid="usml-cat-source-top"
            >
              View on eCFR <ExternalLink className="w-3 h-3" strokeWidth={2} />
            </a>
          )}
        </div>
      </div>

      {/* Hero */}
      <section className="border-b border-hairline" data-testid={`usml-cat-hero-${romanUpper}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-14 pb-10">
          <Link to="/usml" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-ink-soft hover:text-brass transition-colors" data-testid="usml-cat-back">
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} /> All USML categories
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mt-6">
            <div className="lg:col-span-8">
              <div className="flex flex-wrap items-baseline gap-x-6 gap-y-3">
                <span className="font-mono text-4xl sm:text-5xl font-semibold text-brass-deep" data-testid="usml-cat-code">
                  {romanUpper}
                </span>
                <Eyebrow>USML Category</Eyebrow>
              </div>
              <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-ink leading-[1.05] tracking-tight mt-4 max-w-4xl" data-testid="usml-cat-title">
                {entry?.title || "Loading…"}
              </h1>
            </div>
            <div className="lg:col-span-4 lg:text-right space-y-1">
              <p className="section-num">{entry?.paragraph_count ?? "—"}</p>
              <p className="text-[11px] uppercase tracking-[0.22em] text-ink-soft">Top-level paragraphs</p>
              <p className="text-[11px] uppercase tracking-[0.22em] text-ink-soft mt-3">Last modified {lastMod}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Body — search + paragraph list */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-12" data-testid="usml-cat-body">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-40" data-testid="usml-cat-sidebar">
              <div className="relative mb-6">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" strokeWidth={2} />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Filter paragraphs"
                  className="w-full pl-10 pr-3 py-2 bg-ivory border border-hairline text-[13px] text-ink placeholder:text-ink-soft/70 focus:outline-none focus:border-brass"
                  data-testid="usml-cat-filter"
                />
              </div>
              {q && (
                <p className="text-[11px] uppercase tracking-[0.22em] text-ink-soft mb-4" data-testid="usml-cat-filter-count">
                  {filteredParas.length} match{filteredParas.length === 1 ? "" : "es"}
                </p>
              )}
              <p className="text-[10px] uppercase tracking-[0.28em] text-brass-deep font-semibold mb-3">Paragraph index</p>
              <ul className="space-y-0.5">
                {(entry?.paragraph_index || []).map((p) => {
                  if (p.reserved) {
                    return (
                      <li key={p.letter}>
                        <span
                          className="flex items-baseline gap-2 py-1.5 border-b border-hairline-soft text-ink-soft/60 cursor-default"
                          data-testid={`usml-cat-toc-${p.letter}`}
                        >
                          <span className="font-mono text-[12px] font-semibold shrink-0 w-12">({p.letter})</span>
                          <span className="text-[13px] leading-tight truncate italic">[Reserved]</span>
                        </span>
                      </li>
                    );
                  }
                  return (
                    <li key={p.letter}>
                      <Link
                        to={`/usml/e/${romanUpper}/${p.letter}`}
                        className="flex items-baseline gap-2 py-1.5 border-b border-hairline-soft text-ink hover:text-brass transition-colors"
                        data-testid={`usml-cat-toc-${p.letter}`}
                      >
                        <span className="font-mono text-[12px] font-semibold shrink-0 w-12">({p.letter})</span>
                        <span className="text-[13px] leading-tight truncate">{(p.heading || "").split(/[,;.]/)[0].slice(0, 40)}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          </aside>

          {/* Main — paragraph cards */}
          <div className="lg:col-span-9">
            {!entry && <p className="text-ink-soft">Loading paragraphs…</p>}
            {entry && filteredParas.length === 0 && q.trim() && (
              <p className="text-ink-soft" data-testid="usml-cat-no-match">No paragraphs match &ldquo;{q}&rdquo;.</p>
            )}
            {entry && filteredParas.length > 0 && (
              <ul className="border-t border-hairline">
                {filteredParas.map((p) => {
                  const reserved = p.reserved;
                  const to = reserved ? undefined : `/usml/e/${romanUpper}/${p.letter}`;
                  const Wrap = reserved ? "div" : Link;
                  const props = reserved
                    ? { className: "group block py-6 border-b border-hairline opacity-60 cursor-default" }
                    : { to, className: "group block py-6 border-b border-hairline hover:bg-ivory-alt transition-colors" };
                  return (
                    <li key={p.letter} data-testid={`usml-cat-para-${p.letter}`}>
                      <Wrap {...props}>
                        <div className="grid grid-cols-12 gap-4 items-start">
                          <div className="col-span-2 sm:col-span-1">
                            <span className="font-mono text-lg font-semibold text-brass-deep">({p.letter})</span>
                          </div>
                          <div className="col-span-10 sm:col-span-10">
                            {reserved ? (
                              <p className="text-[15px] text-ink-soft italic leading-snug">[Reserved]</p>
                            ) : (
                              <>
                                <p className="text-[15px] sm:text-base text-ink leading-snug font-medium group-hover:text-brass transition-colors">
                                  {p.heading || p.excerpt}
                                </p>
                                {p.excerpt && p.excerpt !== p.heading && (
                                  <p className="mt-2 text-[13px] text-ink-soft leading-snug">
                                    {p.excerpt.slice(0, 180)}{p.excerpt.length > 180 ? "…" : ""}
                                  </p>
                                )}
                              </>
                            )}
                          </div>
                          {!reserved && (
                            <div className="col-span-12 sm:col-span-1 sm:text-right">
                              <ArrowUpRight className="w-4 h-4 text-ink-soft group-hover:text-brass inline-block transition-colors" strokeWidth={2} />
                            </div>
                          )}
                        </div>
                      </Wrap>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </section>

      {/* Bottom disclaimer */}
      <section className="bg-ink text-ivory" data-testid="usml-cat-banner-bottom">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-12 text-center">
          <p className="text-[11px] uppercase tracking-[0.28em] text-brass-light font-semibold mb-4">Verify Before Relying</p>
          <p className="font-serif text-xl sm:text-2xl leading-snug tracking-tight">
            This is a mirror of USML Category {romanUpper} from eCFR{sourceAsOf !== "—" ? ` as of ${sourceAsOf}` : ""}. Nothing on this page is legal advice.
          </p>
          {entry?.source_url && (
            <a
              href={entry.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-8 bg-ivory text-ink px-6 py-3 text-[11px] tracking-[0.22em] uppercase font-semibold hover:bg-brass hover:text-ivory transition-colors"
              data-testid="usml-cat-source-bottom"
            >
              View Category {romanUpper} on eCFR
              <ExternalLink className="w-4 h-4" strokeWidth={2} />
            </a>
          )}
        </div>
      </section>
    </Layout>
  );
}
